/**
 * Render functions for invoice line/freight/restocking/ignored numeric cells.
 * Extracted to keep useInvoiceDetailFormState under 750 lines.
 */
import React from 'react';
import { Box, Center, HStack, Icon, IconButton, Input, Tooltip } from '@chakra-ui/react';
import { TbArrowBackUp } from 'react-icons/tb';
import type { InvoiceDetailResponse } from '../../types/invoice';
import { parseSignedDecimalBlur, SIGNED_DECIMAL_INPUT_RE, UNSIGNED_DECIMAL_INPUT_RE } from '../../utils/invoiceNumericInput';

const ACCENT = '#0071E3';
const fieldLabelMap: Record<string, string> = { listPrice: 'List Price', unitBuyPrice: 'Buy Price', totalBuyPrice: 'Total' };

function fmtDollar(val: string): string {
    return val.startsWith('-') ? `-$${val.slice(1)}` : `$${val}`;
}

type LineItemField = keyof InvoiceDetailResponse['lineItems'][0];

const LINE_GRID_PRICE_COL_MIN = 3;
const PRICE_FIELDS_FOR_GRID: Array<'listPrice' | 'unitBuyPrice' | 'totalBuyPrice'> = ['listPrice', 'unitBuyPrice', 'totalBuyPrice'];

/** Next focused price `cellKey` when tabbing between paste-grid price cells (`${prefix}-${row}-${field}`). */
export function priceCellKeyFromPasteTdRelatedTarget(related: EventTarget | null, cellKeyPrefix: string): string | null {
    if (!(related instanceof Element)) return null;
    const td = related.closest('td[data-line-paste-cell]');
    if (!td) return null;
    const col = parseInt(td.getAttribute('data-col') || '', 10);
    const row = parseInt(td.getAttribute('data-row') || '', 10);
    if (!Number.isFinite(col) || !Number.isFinite(row) || col < LINE_GRID_PRICE_COL_MIN || col > 5) return null;
    const field = PRICE_FIELDS_FOR_GRID[col - LINE_GRID_PRICE_COL_MIN];
    return `${cellKeyPrefix}-${row}-${field}`;
}

export interface RenderLineItemNumericCellProps {
    index: number;
    field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice';
    fw?: string;
    editingNumericField: string | null;
    editingNumericValues: Record<string, string>;
    setEditingNumericField: (v: string | null) => void;
    setEditingNumericValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    getLineItemValue: (index: number, field: LineItemField) => unknown;
    getLineItemPriceValue: (index: number, field: LineItemField, def?: string) => string;
    updateLineItem: (index: number, field: LineItemField, value: any) => void;
    isLineItemFieldEdited: (index: number, field: LineItemField) => boolean;
    openRevertModal: (key: string, label: string) => void;
    isReadOnly?: boolean;
    /** Hide "Value changed" revert (e.g. after Match from NEX replaced rows). */
    suppressValueChangedRevert?: boolean;
    /** Tab between price cells keeps editing state without clearing between blurs. */
    lineItemGridPriceFocusHandoff?: boolean;
}

export function renderLineItemNumericCell(props: RenderLineItemNumericCellProps): React.ReactNode {
    const {
        index,
        field,
        fw,
        editingNumericField,
        editingNumericValues,
        setEditingNumericField,
        setEditingNumericValues,
        getLineItemValue,
        getLineItemPriceValue,
        updateLineItem,
        isLineItemFieldEdited,
        openRevertModal,
        isReadOnly,
        suppressValueChangedRevert,
        lineItemGridPriceFocusHandoff,
    } = props;
    const cellKey = `lineItem-${index}-${field}`;
    const isEditing = editingNumericField === cellKey;
    const edited = !suppressValueChangedRevert && isLineItemFieldEdited(index, field);
    const onPriceInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const raw = editingNumericValues[cellKey] ?? getLineItemValue(index, field)?.toString() ?? '';
        const parsed =
            field === 'totalBuyPrice' ? parseSignedDecimalBlur(raw) : raw === '' ? 0 : parseFloat(raw) || 0;
        updateLineItem(index, field, parsed);
        setEditingNumericValues((p) => {
            const n = { ...p };
            delete n[cellKey];
            return n;
        });
        if (lineItemGridPriceFocusHandoff) {
            const nextKey = priceCellKeyFromPasteTdRelatedTarget(e.relatedTarget, 'lineItem');
            if (nextKey) {
                setEditingNumericField(nextKey);
                return;
            }
        }
        setEditingNumericField(null);
    };
    const priceCellPr = edited ? 5 : 0;
    return (
        <Box w="100%" position="relative" pr={priceCellPr}>
            <HStack spacing={0} align="center" justify="flex-end" whiteSpace="nowrap" flexShrink={0} w="100%">
                {isEditing ? (
                    <Input
                        value={editingNumericValues[cellKey] ?? getLineItemValue(index, field)?.toString() ?? ''}
                        onChange={(e) => {
                            const v = e.target.value;
                            const ok = field === 'totalBuyPrice' ? v === '' || SIGNED_DECIMAL_INPUT_RE.test(v) : v === '' || UNSIGNED_DECIMAL_INPUT_RE.test(v);
                            if (ok) setEditingNumericValues((prev) => ({ ...prev, [cellKey]: v }));
                        }}
                        onBlur={onPriceInputBlur}
                        onKeyDown={
                            lineItemGridPriceFocusHandoff
                                ? undefined
                                : (ev) => ev.key === 'Enter' && (ev.currentTarget as HTMLInputElement).blur()
                        }
                        autoFocus textAlign="right" border="none" outline="1.5px solid" outlineColor="blue.300" borderRadius="md" color="gray.900" bg="blue.50"
                        _focus={{ outline: '1.5px solid', outlineColor: ACCENT, boxShadow: 'none' }}
                        onFocus={(e) => {
                            const input = e.target as HTMLInputElement;
                            if (editingNumericValues[cellKey] === undefined) {
                                setEditingNumericValues((p) => ({ ...p, [cellKey]: getLineItemValue(index, field)?.toString() ?? '' }));
                            }
                            // Full select() traps ArrowLeft/Right in caretBlocks until the user collapses the selection.
                            // For grid handoff, keep a collapsed caret at end (like Editable onFocusEnd) so arrows move cells predictably.
                            if (lineItemGridPriceFocusHandoff) {
                                queueMicrotask(() => {
                                    const len = input.value.length;
                                    try {
                                        input.setSelectionRange(len, len);
                                    } catch {
                                        /* ignore */
                                    }
                                });
                            } else {
                                input.select();
                            }
                        }}
                        fontSize="sm"
                        py="2px"
                        px={2}
                        h="auto"
                        lineHeight="short"
                        fontWeight={fw}
                        w="100%"
                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                    />
                ) : (
                    <Box
                        data-line-item-price-landing={lineItemGridPriceFocusHandoff && !isReadOnly ? '' : undefined}
                        tabIndex={lineItemGridPriceFocusHandoff && !isReadOnly ? 0 : undefined}
                        onClick={() => setEditingNumericField(cellKey)}
                        cursor="pointer"
                        w="100%"
                        textAlign="right"
                        px={2}
                        fontWeight={fw}
                        color="gray.900"
                        whiteSpace="nowrap"
                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                        minW={0}
                        outline="none"
                        _focusVisible={{ boxShadow: 'inset 0 0 0 2px var(--chakra-colors-blue-500)' }}
                    >
                        {fmtDollar(getLineItemPriceValue(index, field, '0.00'))}
                    </Box>
                )}
            </HStack>
            {!isReadOnly && edited ? (
                <Box position="absolute" right={0} top="50%" transform="translateY(-50%)" w="16px" display="flex" justifyContent="center" pointerEvents="none">
                    <Tooltip label="Value changed" placement="top" hasArrow>
                        <Center as="span" w="18px" h="18px" minW="18px" minH="18px" borderRadius="full" border="1.5px solid" borderColor="orange.400" display="inline-flex" flexShrink={0} pointerEvents="auto">
                            <IconButton
                                aria-label="Compare"
                                icon={<Icon as={TbArrowBackUp as React.ElementType} w={3} h={3} color="orange.500" />}
                                size="xs"
                                variant="ghost"
                                onClick={() => openRevertModal(`lineItem-${index}-${field}`, `Line ${index + 1} – ${fieldLabelMap[field] ?? field}`)}
                                _hover={{ bg: 'orange.50' }}
                                minW="auto"
                                h="auto"
                                p={0}
                                flexShrink={0}
                                tabIndex={lineItemGridPriceFocusHandoff ? -1 : undefined}
                            />
                        </Center>
                    </Tooltip>
                </Box>
            ) : null}
        </Box>
    );
}

type GenericNumericPasteGridOpts = { handoff: boolean; rowKeyPrefix: string; isReadOnly?: boolean };

function genericNumericCell(
    cellKey: string,
    isEditing: boolean,
    displayValue: string,
    numValue: number,
    editingNumericValues: Record<string, string>,
    setEditingNumericField: (v: string | null) => void,
    setEditingNumericValues: React.Dispatch<React.SetStateAction<Record<string, string>>>,
    onBlur: (parsed: number) => void,
    fw?: string,
    pasteGrid?: GenericNumericPasteGridOpts,
    allowSignedDecimal = false
): React.ReactNode {
    const handoff = !!pasteGrid?.handoff;
    const ro = !!pasteGrid?.isReadOnly;
    const rowPrefix = pasteGrid?.rowKeyPrefix ?? '';

    const onInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const raw = editingNumericValues[cellKey] ?? String(numValue);
        onBlur(allowSignedDecimal ? parseSignedDecimalBlur(raw) : parseFloat(raw) || 0);
        setEditingNumericValues((p) => {
            const n = { ...p };
            delete n[cellKey];
            return n;
        });
        if (handoff && rowPrefix) {
            const nextKey = priceCellKeyFromPasteTdRelatedTarget(e.relatedTarget, rowPrefix);
            if (nextKey) {
                setEditingNumericField(nextKey);
                return;
            }
        }
        setEditingNumericField(null);
    };

    return (
        <HStack spacing={0} align="center" justify="flex-end" whiteSpace="nowrap" flexShrink={0} w="100%">
            {isEditing ? (
                <Input
                    value={editingNumericValues[cellKey] ?? String(numValue)}
                    onChange={(e) => {
                        const v = e.target.value;
                        const ok = allowSignedDecimal ? v === '' || SIGNED_DECIMAL_INPUT_RE.test(v) : v === '' || UNSIGNED_DECIMAL_INPUT_RE.test(v);
                        if (ok) setEditingNumericValues((p) => ({ ...p, [cellKey]: v }));
                    }}
                    onBlur={onInputBlur}
                    onKeyDown={handoff ? undefined : (e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
                    autoFocus textAlign="right" border="none" outline="1.5px solid" outlineColor="blue.300" borderRadius="md" color="gray.900" bg="blue.50"
                    _focus={{ outline: '1.5px solid', outlineColor: ACCENT, boxShadow: 'none' }}
                    onFocus={(e) => {
                        const input = e.target as HTMLInputElement;
                        if (editingNumericValues[cellKey] === undefined) {
                            setEditingNumericValues((p) => ({ ...p, [cellKey]: String(numValue) }));
                        }
                        if (handoff) {
                            queueMicrotask(() => {
                                const len = input.value.length;
                                try {
                                    input.setSelectionRange(len, len);
                                } catch {
                                    /* ignore */
                                }
                            });
                        } else {
                            input.select();
                        }
                    }}
                    fontSize="sm" py="2px" px={2} h="auto" lineHeight="short" fontWeight={fw} w="100%"
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                />
            ) : (
                <Box
                    data-line-item-price-landing={handoff && !ro ? true : undefined}
                    tabIndex={handoff && !ro ? 0 : undefined}
                    onClick={() => setEditingNumericField(cellKey)}
                    cursor="pointer"
                    w="100%"
                    textAlign="right"
                    px={2}
                    fontWeight={fw}
                    color="gray.900"
                    whiteSpace="nowrap"
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                    minW={0}
                    outline="none"
                    _focusVisible={handoff && !ro ? { boxShadow: 'inset 0 0 0 2px var(--chakra-colors-blue-500)' } : undefined}
                >
                    {displayValue}
                </Box>
            )}
        </HStack>
    );
}

export interface RenderFreightItemNumericCellProps {
    index: number;
    field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice';
    fw?: string;
    editingNumericField: string | null;
    editingNumericValues: Record<string, string>;
    setEditingNumericField: (v: string | null) => void;
    setEditingNumericValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    getFreightItemValue: (index: number, field: LineItemField) => unknown;
    getFreightItemPriceValue: (index: number, field: LineItemField, def?: string) => string;
    updateFreightItem: (index: number, field: LineItemField, value: any) => void;
    isReadOnly?: boolean;
    /** Tab between price cells + paste-grid landing (same as line items). */
    pasteGridPriceHandoff?: boolean;
}

export function renderFreightItemNumericCell(props: RenderFreightItemNumericCellProps): React.ReactNode {
    const {
        index,
        field,
        fw,
        editingNumericField,
        editingNumericValues,
        setEditingNumericField,
        setEditingNumericValues,
        getFreightItemValue,
        getFreightItemPriceValue,
        updateFreightItem,
        isReadOnly,
        pasteGridPriceHandoff,
    } = props;
    const cellKey = `freightItem-${index}-${field}`;
    const val = getFreightItemValue(index, field);
    const numVal = typeof val === 'number' ? val : parseFloat(String(val)) || 0;
    const pasteGrid =
        pasteGridPriceHandoff && !isReadOnly ? { handoff: true, rowKeyPrefix: 'freightItem', isReadOnly: !!isReadOnly } : undefined;
    return genericNumericCell(
        cellKey,
        editingNumericField === cellKey,
        fmtDollar(getFreightItemPriceValue(index, field, '0.00')),
        numVal,
        editingNumericValues,
        setEditingNumericField,
        setEditingNumericValues,
        (parsed) => updateFreightItem(index, field, parsed),
        fw,
        pasteGrid,
        field === 'totalBuyPrice'
    );
}

export interface RenderFuelItemNumericCellProps {
    index: number;
    field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice';
    fw?: string;
    editingNumericField: string | null;
    editingNumericValues: Record<string, string>;
    setEditingNumericField: (v: string | null) => void;
    setEditingNumericValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    getFuelItemValue: (index: number, field: LineItemField) => unknown;
    getFuelItemPriceValue: (index: number, field: LineItemField, def?: string) => string;
    updateFuelItem: (index: number, field: LineItemField, value: any) => void;
}

export function renderFuelItemNumericCell(props: RenderFuelItemNumericCellProps): React.ReactNode {
    const { index, field, fw, editingNumericField, editingNumericValues, setEditingNumericField, setEditingNumericValues, getFuelItemValue, getFuelItemPriceValue, updateFuelItem } = props;
    const cellKey = `fuelItem-${index}-${field}`;
    const val = getFuelItemValue(index, field);
    const numVal = typeof val === 'number' ? val : parseFloat(String(val)) || 0;
    return genericNumericCell(
        cellKey,
        editingNumericField === cellKey,
        fmtDollar(getFuelItemPriceValue(index, field, '0.00')),
        numVal,
        editingNumericValues,
        setEditingNumericField,
        setEditingNumericValues,
        (parsed) => updateFuelItem(index, field, parsed),
        fw,
        undefined,
        field === 'totalBuyPrice'
    );
}

export interface RenderRestockingFeeItemNumericCellProps {
    index: number;
    field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice';
    fw?: string;
    editingNumericField: string | null;
    editingNumericValues: Record<string, string>;
    setEditingNumericField: (v: string | null) => void;
    setEditingNumericValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    getRestockingFeeItemValue: (index: number, field: LineItemField) => unknown;
    getRestockingFeeItemPriceValue: (index: number, field: LineItemField, def?: string) => string;
    updateRestockingFeeItem: (index: number, field: LineItemField, value: any) => void;
    isReadOnly?: boolean;
    pasteGridPriceHandoff?: boolean;
}

export function renderRestockingFeeItemNumericCell(props: RenderRestockingFeeItemNumericCellProps): React.ReactNode {
    const {
        index,
        field,
        fw,
        editingNumericField,
        editingNumericValues,
        setEditingNumericField,
        setEditingNumericValues,
        getRestockingFeeItemValue,
        getRestockingFeeItemPriceValue,
        updateRestockingFeeItem,
        isReadOnly,
        pasteGridPriceHandoff,
    } = props;
    const cellKey = `restockingFeeItem-${index}-${field}`;
    const val = getRestockingFeeItemValue(index, field);
    const numVal = typeof val === 'number' ? val : parseFloat(String(val)) || 0;
    const pasteGrid =
        pasteGridPriceHandoff && !isReadOnly ? { handoff: true, rowKeyPrefix: 'restockingFeeItem', isReadOnly: !!isReadOnly } : undefined;
    return genericNumericCell(
        cellKey,
        editingNumericField === cellKey,
        fmtDollar(getRestockingFeeItemPriceValue(index, field, '0.00')),
        numVal,
        editingNumericValues,
        setEditingNumericField,
        setEditingNumericValues,
        (parsed) => updateRestockingFeeItem(index, field, parsed),
        fw,
        pasteGrid,
        field === 'totalBuyPrice'
    );
}

export interface RenderIgnoredPartNumericCellProps {
    index: number;
    field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice';
    fw?: string;
    editingNumericField: string | null;
    editingNumericValues: Record<string, string>;
    setEditingNumericField: (v: string | null) => void;
    setEditingNumericValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    getIgnoredPartValue: (index: number, field: LineItemField) => unknown;
    getIgnoredPartPriceValue: (index: number, field: LineItemField, def?: string) => string;
    updateIgnoredPart: (index: number, field: LineItemField, value: any) => void;
    isReadOnly?: boolean;
    pasteGridPriceHandoff?: boolean;
}

export function renderIgnoredPartNumericCell(props: RenderIgnoredPartNumericCellProps): React.ReactNode {
    const {
        index,
        field,
        fw,
        editingNumericField,
        editingNumericValues,
        setEditingNumericField,
        setEditingNumericValues,
        getIgnoredPartValue,
        getIgnoredPartPriceValue,
        updateIgnoredPart,
        isReadOnly,
        pasteGridPriceHandoff,
    } = props;
    const cellKey = `ignoredPart-${index}-${field}`;
    const val = getIgnoredPartValue(index, field);
    const numVal = typeof val === 'number' ? val : parseFloat(String(val)) || 0;
    const pasteGrid =
        pasteGridPriceHandoff && !isReadOnly ? { handoff: true, rowKeyPrefix: 'ignoredPart', isReadOnly: !!isReadOnly } : undefined;
    return genericNumericCell(
        cellKey,
        editingNumericField === cellKey,
        fmtDollar(getIgnoredPartPriceValue(index, field, '0.00')),
        numVal,
        editingNumericValues,
        setEditingNumericField,
        setEditingNumericValues,
        (parsed) => updateIgnoredPart(index, field, parsed),
        fw,
        pasteGrid,
        field === 'totalBuyPrice'
    );
}
